import { useQueryClient, useMutation, useQuery } from 'react-query';
import { useMemo } from 'react';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import * as api from '@ubeya/shared/services/api';

function* generatorId() {
  let i = -1;
  while (true) {
    yield i;
    // eslint-disable-next-line no-plusplus
    i--;
  }
}

const counter = generatorId();

const useEmployeeTimeAvailabilities = ({ accountId, employeeId, from, to, refetchOnAvailabilityChange }) => {
  const storeKey = ['timeAvailabilities', accountId, employeeId, from, to];

  const queryClient = useQueryClient();
  const { data: userData } = useUser();

  const { isLoading, data, refetch } = useQuery(
    storeKey,
    () =>
      api.fetchEmployeesTimeAvailabilities({
        accountId,
        employeeIds: [employeeId],
        from,
        to
      }),
    {
      enabled: !!userData?.id,
      //   retry: false,
      //   cacheTime: Infinity,
      //   staleTime: Infinity,
      cacheTime: 0,
      staleTime: 0
    }
  );

  const { timeAvailabilities, timeOffs, dailyAvailability } = useMemo(
    () => ({
      timeAvailabilities: data?.timeAvailabilities || [],
      timeOffs: (data?.timeOffs || []).filter((timeOff) => !!timeOff.timeoffId),
      dailyAvailability:
        (data?.dailyAvailability || []).find((entry) => entry.employeeId === employeeId)?.dailyAvailability || []
    }),
    [data?.timeAvailabilities, data?.timeOffs, data?.dailyAvailability, employeeId]
  );

  const { mutateAsync: addTimeAvailabilities } = useMutation(
    ({ timeAvailabilities: timeAvailabilitiesData }) =>
      api.addTimeAvailabilities({ accountId, timeAvailabilities: timeAvailabilitiesData, employeeId }),
    {
      onMutate: ({ timeAvailabilities: timeAvailabilitiesData }) => {
        const prevData = queryClient.getQueryData(storeKey);
        const addedData = timeAvailabilitiesData.map((timeAvailability) => ({
          ...timeAvailability,
          id: counter.next().value
        }));

        return queryClient.setQueryData(storeKey, {
          ...prevData,
          timeAvailabilities: [...(prevData?.timeAvailabilities || []), ...addedData]
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        refetchOnAvailabilityChange?.();
      }
    }
  );

  const { mutateAsync: deleteTimeAvailability } = useMutation(
    ({ id }) => api.deleteTimeAvailability({ timeAvailabilityId: id, accountId }),
    {
      onMutate: ({ id }) => {
        const prevData = queryClient.getQueryData(storeKey);
        const newData = {
          ...prevData,
          timeAvailabilities: prevData?.timeAvailabilities.filter(({ id: cId }) => id !== cId)
        };

        return queryClient.setQueryData(storeKey, newData);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        refetchOnAvailabilityChange?.();
      }
    }
  );

  const { mutateAsync: deleteTimeOff } = useMutation(({ id }) => api.deleteTimeOff({ timeOffId: id, accountId }), {
    onMutate: ({ id }) => {
      const prevData = queryClient.getQueryData(storeKey);
      const newData = {
        ...prevData,
        timeOffs: prevData?.timeOffs.filter(({ id: cId }) => id !== cId)
      };

      return queryClient.setQueryData(storeKey, newData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(storeKey);
      refetchOnAvailabilityChange?.();
    }
  });

  return {
    timeAvailabilities,
    deleteTimeOff,
    timeOffs,
    dailyAvailability,
    addTimeAvailabilities,
    deleteTimeAvailability,
    refetch,
    isLoading
  };
};

export default useEmployeeTimeAvailabilities;
